import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { ExternalLink } from "lucide-react";
import { TID } from "@/lib/testIds";

// Exact embed as provided (DR-DATA AUTO) — konf.dr-file.de
const KONF_SRC =
  "https://konf.vpt.fileservice.app/index.php?lic=tgkjddiofjsiojfs646&origin=vpt.fileservice.app&theme=custom&make=Audi&model=A7&gen=4G%202010%20-%202014&engine=3.0%20TDI%20Bi-Turbo%20313hp&lang=de&bg=transparent&text=%23ffffff&accent=%23fbff00&pulse=%23fff700&pulseOff=0&dyno=1&dynoBg=%230a0a0a&dynoText=%23ffffff&dynoAccent=%23ffea00";

export default function Configurator() {
  const iframeRef = useRef(null);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (iframe && !iframe.src) iframe.src = KONF_SRC;

    const onMessage = (e) => {
      if (e.data && e.data.type === "resize" && iframeRef.current) {
        iframeRef.current.style.height = e.data.height + "px";
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  return (
    <section
      id="konfigurator-section"
      data-testid={TID.konfigurator.root}
      className="relative py-24 md:py-32 bg-[#070707] border-y border-white/10 overflow-hidden"
    >
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[600px] w-[900px] rounded-full blur-[160px] opacity-20"
        style={{ background: "radial-gradient(circle, #FFEA00 0%, transparent 70%)" }}
        aria-hidden="true"
      />
      <div className="relative max-w-6xl mx-auto px-5 md:px-10">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <p className="text-sm uppercase tracking-[0.35em] text-accent mb-4">Live Konfigurator</p>
          <h2 className="font-display font-bold uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
            Dein Fahrzeug.<br /><span className="text-accent">Deine Leistung.</span>
          </h2>
          <p className="mt-6 text-white/55 leading-relaxed">
            Wähle Marke, Modell und Motorisierung und sieh sofort das mögliche Leistungsplus für dein Fahrzeug.
          </p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="relative border border-accent/30 bg-black shadow-[0_0_60px_rgba(255,234,0,0.15)]"
        >
          <div className="flex items-center justify-between px-5 py-3 border-b border-white/10 bg-white/[0.02]">
            <div className="flex items-center gap-2">
              <span className="h-2.5 w-2.5 rounded-full bg-primary animate-vpt-pulse" />
              <span className="font-display uppercase tracking-widest text-xs text-white/60">VPT Fahrzeug-Konfigurator</span>
            </div>
            <a
              data-testid={TID.konfigurator.open}
              href={KONF_SRC}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-accent hover:text-white transition-colors"
            >
              In neuem Tab öffnen <ExternalLink className="h-3.5 w-3.5" />
            </a>
          </div>

          {/* DR-DATA AUTO */}
          <iframe
            id="fahrzeug-konfigurator"
            ref={iframeRef}
            data-testid={TID.konfigurator.iframe}
            data-license="tgkjddiofjsiojfs646"
            data-theme="custom"
            data-lang="de"
            title="VPT Fahrzeug-Konfigurator"
            scrolling="no"
            loading="lazy"
            style={{
              width: "100%",
              maxWidth: 1200,
              margin: "0 auto",
              border: "none",
              display: "block",
              minHeight: "180px",
              background: "transparent",
            }}
          />
          {/* DR-DATA AUTO */}
        </motion.div>
      </div>
    </section>
  );
}
